import React, { forwardRef, useMemo, useState } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { TouchableOpacity } from 'react-native-gesture-handler'
import { ActivityIndicator } from 'react-native-paper'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetTextInput,
  BottomSheetView,
  useBottomSheetModal
} from '@gorhom/bottom-sheet'

import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'

import { DS } from '../../design-system'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { ReduceMotion } from 'react-native-reanimated'
import { deleteAccountSheetModal } from './DeleteAccountSheetModal'
import { useThrottledCallback } from 'use-debounce'
import { useMaxWidth } from '@/components/WebWrapper'



export enum EditableFields {
  name = 'name',
  email = 'email'
}

const isWeb = Platform.OS === 'web'

export const EditProfileSheet = forwardRef<BottomSheetModal>((props, ref) => {
  const maxWidth = useMaxWidth()
  const insets = useSafeAreaInsets()
  const {
    session: { user, updateUserNameOnAppAndWordpress, checkIsUsernameTaken }
  } = useStore()
  const [userName, setUserName] = useState(user ? user.name?.slice(0, 15) : '')
  const [userEmail, setUserEmail] = useState(user ? user.email : '')
  const [isLoading, setIsLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const { dismissAll } = useBottomSheetModal()

  const snapPoints = useMemo(() => ['80%'], [])

  const deleteUser = useThrottledCallback(() => {
    dismissAll()
    // Open delete account
    deleteAccountSheetModal.current?.present()
  }, 350)

  const content = {
    [EditableFields.name]: {
      question: translate('editProfileScreen.nameQuestion'),
      label: translate('editProfileScreen.nameLabel'),
      placeholder: translate('editProfileScreen.namePlaceholder'),
      currentValue: userName
    },
    [EditableFields.email]: {
      question: translate('editProfileScreen.emailQuestion'),
      label: translate('editProfileScreen.emailLabel'),
      placeholder: translate('editProfileScreen.emailPlaceholder'),
      currentValue: userEmail
    }
  }

  const reportProfileCompleted = useMutation(api.badges.mutations.reportProfileCompleted)

  const updateUserField = async () => {
    try {
      setIsLoading(true)
      if (userName) {
        const isUsernameTaken = await checkIsUsernameTaken(userName)
        if (!isUsernameTaken) {
          await updateUserNameOnAppAndWordpress(userName)
          reportProfileCompleted({}).catch(() => {})
          dismissAll()
        } else {
          setErrorMsg(`The username ${userName} is already taken`)
        }
        setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false)
      //@ts-ignore
      setErrorMsg(error.response.errors[0].message)
    }
  }
  const invalidUserName = (userName && userName.trim().length === 0) || userName === ''
  const styles = editProfileSheetStyles

  return (
    <BottomSheetModal
      name="edit-profile"
      ref={ref}
      index={0}
      snapPoints={snapPoints}
      enableDynamicSizing
      backdropComponent={(props) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />}
      keyboardBehavior="extend"
      keyboardBlurBehavior="restore"
      backgroundStyle={styles.background}
      handleIndicatorStyle={styles.handleIndicator}
      overrideReduceMotion={ReduceMotion.System}
      style={{
        marginHorizontal: isWeb ? 'auto' : undefined,
        maxWidth: maxWidth,
        width: '100%'
      }}
    >
      <BottomSheetView style={isWeb ? {} : { flex: 1 }}>
        <DS.Frame style={[styles.wrapper, { paddingBottom: insets.bottom }]}>
          <DS.Text variant="Heading 3/Medium">Edit Profile</DS.Text>
          <DS.Frame style={[styles.container]}>
            <View style={styles.formWrapper}>
              <View style={styles.inputWrapper}>
                <DS.Frame row justifyContent="space-between">
                  <DS.Text variant="Body Medium/Medium">Name:</DS.Text>
                  <DS.Text variant="Body Medium/Regular">{`${content.name.currentValue?.length}/15`}</DS.Text>
                </DS.Frame>
                <BottomSheetTextInput
                  placeholder={content.name.placeholder}
                  placeholderTextColor={DS.palette['Brand/Dark Grey']}
                  style={[
                    styles.textInput,
                    DS.typography['Body Medium/Regular'],
                    { borderColor: errorMsg.length ? DS.palette['Utility/Warning'] : DS.palette['Global/Black'] }
                  ]}
                  allowFontScaling={false}
                  autoCorrect={false}
                  maxLength={15}
                  autoComplete="off"
                  autoCapitalize="none"
                  value={content.name.currentValue}
                  onChangeText={(value) => {
                    setErrorMsg('')
                    setUserName(value)
                  }}
                  autoFocus={!isWeb}
                />
                <DS.Text variant="Body Small/Regular" color={'Utility/Warning'}>
                  {errorMsg}
                </DS.Text>
              </View>

              <View style={styles.inputWrapper}>
                <DS.Text style={{ height: 20 }} variant="Body Medium/Medium">
                  Email:
                </DS.Text>
                <BottomSheetTextInput
                  placeholder={content.email.placeholder}
                  placeholderTextColor={'#68706D'}
                  editable={false}
                  selectTextOnFocus={false}
                  style={[
                    styles.textInput,
                    {
                      backgroundColor: DS.palette['Brand/Light Grey'],
                      borderColor: DS.palette['Brand/Light Grey']
                    },
                    DS.typography['Body Medium/Regular']
                  ]}
                  allowFontScaling={false}
                  autoCorrect={false}
                  maxLength={50}
                  autoComplete="off"
                  autoCapitalize="none"
                  value={content.email.currentValue ?? ''}
                  onChangeText={(value) => setUserEmail(value)}
                  autoFocus={!isWeb}
                />
              </View>
            </View>
            <View
              style={{
                marginVertical: 12
              }}
            >
              <View style={styles.divider} />
            </View>

            <TouchableOpacity onPress={deleteUser} style={styles.buttonDelete} hitSlop={10}>
              <DS.Text variant="Body Small/Regular" color="Brand/Maroon">
                Delete Account
              </DS.Text>
            </TouchableOpacity>
          </DS.Frame>

          <DS.Frame row style={styles.buttonsContainer}>
            <TouchableOpacity
              disabled={invalidUserName}
              onPress={updateUserField}
              style={[
                styles.btn,
                {
                  backgroundColor: invalidUserName ? DS.palette['Brand/Light Grey'] : DS.palette['Global/Black'],
                  opacity: invalidUserName ? 0.3 : 1
                }
              ]}
            >
              <DS.Text variant="Body Medium/Medium" color="Global/White" style={{ textAlign: 'center' }}>
                Save Changes
              </DS.Text>
              {isLoading ? <ActivityIndicator size="small" color="#FFFFFF" style={styles.btnLoading} /> : null}
            </TouchableOpacity>
            <TouchableOpacity
              onPress={dismissAll}
              style={[
                styles.btn,
                {
                  backgroundColor: DS.palette['Brand/Cream'],
                  borderColor: DS.palette['Brand/Light Grey']
                }
              ]}
            >
              <DS.Text variant="Body Medium/Medium" color="Global/Black" style={{ textAlign: 'center' }}>
                Cancel
              </DS.Text>
            </TouchableOpacity>
          </DS.Frame>
        </DS.Frame>
      </BottomSheetView>
    </BottomSheetModal>
  )
})

const editProfileSheetStyles = StyleSheet.create({
  background: {
    backgroundColor: DS.palette['Brand/Cream']
  },
  handleIndicator: {
    width: 45,
    backgroundColor: `${DS.palette['Brand/Dark Grey']}40`,
    height: 4
  },
  wrapper: {
    flexGrow: 1,
    paddingHorizontal: DS.spacing[16],
    paddingTop: DS.spacing[12]
  },
  container: {
    flexGrow: 1
  },
  formWrapper: {
    marginTop: 12
  },
  inputWrapper: {
    gap: DS.spacing[8]
  },
  textInput: {
    height: 50,
    padding: DS.spacing[16],
    borderRadius: DS.spacing[4],
    paddingStart: DS.spacing[8],
    borderWidth: 1
  },
  divider: {
    width: '100%',
    backgroundColor: DS.palette['Brand/Light Grey'],
    height: 1
  },
  buttonDelete: {},
  buttonsContainer: {
    marginTop: 16,
    justifyContent: 'space-between',
    paddingVertical: isWeb ? 24 : 0
  },
  btn: {
    flexGrow: 1,
    flexDirection: 'row',
    padding: DS.spacing[16],
    borderRadius: DS.spacing[48],
    borderWidth: 1,
    justifyContent: 'center'
  },
  btnLoading: {
    width: 20,
    height: 20
  }
})
